import { useState, useEffect } from 'react';
import { Save, Loader2, MessageSquare, Info, Smartphone, CheckCircle2 } from 'lucide-react';
import { notificationApi, type SalonNotificationTemplate, type NotificationEventType } from '../../api/notificationApi';
import { useSalonId } from '../../hooks/useSalonId';

const EVENT_LABELS: Record<NotificationEventType, { title: string; desc: string; icon: string }> = {
    APPOINTMENT_CONFIRMATION: {
        title: 'Confirmación de Cita',
        desc: 'Se envía inmediatamente cuando un cliente agenda una cita.',
        icon: '✅'
    },
    APPOINTMENT_REMINDER_24H: {
        title: 'Recordatorio (24 horas)',
        desc: 'Se envía automáticamente un día antes de la cita.',
        icon: '⏰'
    },
    APPOINTMENT_REMINDER_1H: {
        title: 'Recordatorio (1 hora)',
        desc: 'Se envía una hora antes para evitar olvidos de último minuto.',
        icon: '🏃'
    },
    APPOINTMENT_CANCELLATION: {
        title: 'Aviso de Cancelación',
        desc: 'Se envía si la cita es cancelada por el salón o el cliente.',
        icon: '❌'
    }
};

const PLACEHOLDERS = [
    { key: '{clientName}', label: 'Nombre' },
    { key: '{serviceName}', label: 'Servicio' },
    { key: '{employeeName}', label: 'Profesional' },
    { key: '{date}', label: 'Fecha' },
    { key: '{time}', label: 'Hora' },
    { key: '{salonName}', label: 'Local' },
    { key: '{link}', label: 'Link' },
];

const NotificationConfigPanel = () => {
    const salonId = useSalonId();
    const [templates, setTemplates] = useState<Partial<Record<NotificationEventType, SalonNotificationTemplate>>>({});
    const [activeEvent, setActiveEvent] = useState<NotificationEventType>('APPOINTMENT_CONFIRMATION');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const defaultTemplates = notificationApi.getDefaults();

    useEffect(() => {
        const fetchTemplates = async () => {
            if (!salonId) return;
            try {
                const data = await notificationApi.getTemplates(salonId);
                const map: Partial<Record<NotificationEventType, SalonNotificationTemplate>> = {};
                data.forEach(t => { map[t.eventType] = t; });
                setTemplates(map);
            } catch (err) {
                console.error('Error fetching templates:', err);
                setError('No se pudieron cargar las plantillas');
            } finally {
                setIsLoading(false);
            }
        };
        fetchTemplates();
    }, [salonId]);

    const handleBodyChange = (value: string) => {
        setTemplates(prev => ({
            ...prev,
            [activeEvent]: {
                ...(prev[activeEvent] || { salonId: salonId!, eventType: activeEvent, enabled: true }),
                templateBody: value
            } as SalonNotificationTemplate
        }));
        setSuccess(false);
    };

    const handleToggleEnabled = (enabled: boolean) => {
        setTemplates(prev => ({
            ...prev,
            [activeEvent]: {
                ...(prev[activeEvent] || { salonId: salonId!, eventType: activeEvent, templateBody: defaultTemplates[activeEvent] }),
                enabled
            } as SalonNotificationTemplate
        }));
        setSuccess(false);
    };

    const handleSave = async () => {
        if (!salonId || !activeEvent) return;
        setIsSaving(true);
        setError(null);
        try {
            const current = templates[activeEvent] || {
                salonId,
                eventType: activeEvent,
                templateBody: defaultTemplates[activeEvent],
                enabled: true
            };
            const updated = await notificationApi.updateTemplate(salonId, current as SalonNotificationTemplate);
            setTemplates(prev => ({ ...prev, [activeEvent]: updated }));
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            setError('Error al guardar la plantilla');
        } finally {
            setIsSaving(false);
        }
    };

    const insertPlaceholder = (ph: string) => {
        const currentBody = templates[activeEvent]?.templateBody || defaultTemplates[activeEvent];
        handleBodyChange(currentBody + ph);
    };

    if (isLoading) return <div className="flex justify-center p-12"><Loader2 className="animate-spin" /></div>;

    const currentTemplate = templates[activeEvent] || {
        templateBody: defaultTemplates[activeEvent],
        enabled: true
    };

    // Preview processing
    const previewText = (currentTemplate.templateBody || defaultTemplates[activeEvent])
        .replace(/{clientName}/g, 'María')
        .replace(/{serviceName}/g, 'Manicura Pro')
        .replace(/{employeeName}/g, 'Diana')
        .replace(/{date}/g, 'Lunes, 20 de Abr')
        .replace(/{time}/g, '3:00 PM')
        .replace(/{salonName}/g, 'Cresta Studio')
        .replace(/{link}/g, 'cr3sta.com/c/abc123');

    return (
        <div className="space-y-6">
            <div className="grid md:grid-cols-3 gap-6">
                
                {/* Sidebar: Event selector */}
                <div className="space-y-2">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Eventos Automáticos</p>
                    {(Object.keys(EVENT_LABELS) as NotificationEventType[]).map(type => (
                        <button
                            key={type}
                            onClick={() => { setActiveEvent(type); setError(null); setSuccess(false); }}
                            className={`w-full text-left p-4 rounded-2xl border-2 transition-all flex items-center justify-between ${
                                activeEvent === type 
                                ? 'border-emerald-500 bg-emerald-50 shadow-sm' 
                                : 'border-gray-50 bg-white hover:border-gray-200'
                            }`}
                        >
                            <div className="flex items-center gap-3">
                                <span className="text-2xl">{EVENT_LABELS[type].icon}</span>
                                <div className="overflow-hidden">
                                    <p className={`font-bold text-sm truncate ${activeEvent === type ? 'text-emerald-900' : 'text-gray-700'}`}>
                                        {EVENT_LABELS[type].title}
                                    </p>
                                    <p className="text-[10px] text-gray-500 truncate">{templates[type]?.enabled === false ? '🔴 Desactivado' : '🟢 Activo'}</p>
                                </div>
                            </div>
                            {templates[type]?.id && <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" title="Personalizado" />}
                        </button>
                    ))}
                </div>

                {/* Main: Editor & Preview */}
                <div className="md:col-span-2 space-y-6">
                    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden flex flex-col h-full">
                        
                        {/* Header */}
                        <div className="p-6 border-b border-gray-100 bg-gray-50/50">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                                    <MessageSquare className="w-5 h-5 text-emerald-600" />
                                    Editar Mensaje
                                </h3>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-medium text-gray-500">{currentTemplate.enabled ? 'Activo' : 'Desactivado'}</span>
                                    <button 
                                        onClick={() => handleToggleEnabled(!currentTemplate.enabled)}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${currentTemplate.enabled ? 'bg-emerald-500' : 'bg-gray-200'}`}
                                    >
                                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${currentTemplate.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                                    </button>
                                </div>
                            </div>
                            <p className="text-sm text-gray-500">{EVENT_LABELS[activeEvent].desc}</p>
                        </div>

                        <div className="p-6 space-y-4 flex-1">
                            {/* Editor Area */}
                            <div>
                                <div className="flex flex-wrap gap-1.5 mb-3">
                                    {PLACEHOLDERS.map(ph => (
                                        <button 
                                            key={ph.key} 
                                            onClick={() => insertPlaceholder(ph.key)}
                                            className="px-2 py-1 bg-gray-100 text-gray-600 rounded-lg text-[11px] font-bold hover:bg-emerald-100 hover:text-emerald-700 transition-colors"
                                        >
                                            + {ph.label}
                                        </button>
                                    ))}
                                </div>
                                <textarea
                                    value={currentTemplate.templateBody || ''}
                                    onChange={(e) => handleBodyChange(e.target.value)}
                                    placeholder="Escribe tu mensaje aquí..."
                                    rows={6}
                                    className="w-full px-4 py-3 bg-gray-50 rounded-2xl text-sm border-2 border-transparent focus:border-emerald-400 focus:bg-white outline-none transition-all resize-none font-medium"
                                />
                                <div className="mt-2 flex items-center gap-2 text-[11px] text-gray-400 bg-blue-50/50 p-2 rounded-xl">
                                    <Info className="w-3 h-3 text-blue-400" />
                                    Los campos entre llaves como <strong>{'{clientName}'}</strong> se llenarán automáticamente.
                                </div>
                            </div>

                            {/* WhatsApp Preview */}
                            <div>
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                                    <Smartphone className="w-3 h-3" />
                                    Vista Previa en WhatsApp
                                </p>
                                <div className="bg-[#e5ddd5] rounded-3xl p-4 relative overflow-hidden">
                                    {/* Bubble overlay to simulate whatsapp */}
                                    <div className="flex items-end gap-2">
                                        <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 shadow-sm">
                                            {EVENT_LABELS[activeEvent].icon}
                                        </div>
                                        <div className="bg-white rounded-2xl rounded-bl-sm px-4 py-3 max-w-[85%] shadow-md border-b-2 border-black/5">
                                            <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{previewText}</p>
                                            <div className="flex items-center justify-end gap-1 mt-1">
                                                <span className="text-[10px] text-gray-400">12:35 PM</span>
                                                <span className="text-blue-500 text-xs font-bold">✓✓</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {error && <p className="text-xs text-red-500 font-medium">{error}</p>}
                        </div>

                        {/* Footer Action */}
                        <div className="p-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                            <p className="text-[11px] text-gray-400">Los cambios se aplican inmediatamente.</p>
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all shadow-lg hover:-translate-y-0.5 ${
                                    success 
                                    ? 'bg-emerald-50 text-emerald-700 shadow-emerald-100' 
                                    : 'bg-emerald-600 text-white shadow-emerald-200'
                                }`}
                            >
                                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : (success ? <CheckCircle2 className="w-4 h-4" /> : <Save className="w-4 h-4" />)}
                                {isSaving ? 'Guardando...' : (success ? '¡Guardado!' : 'Guardar Cambios')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NotificationConfigPanel;
